import React, { useState } from "react";
import { PageHero } from "./AboutPage";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, CheckCircle2, ExternalLink } from "lucide-react";

const APPLICATION_FEE = 25;

// Toggle this to re-open prospective member applications.
// When false, the "Prospective" track is locked and only general contact
// inquiries are accepted.
const APPLICATIONS_OPEN = false;

const buildCashAppUrl = (applicantName) => {
  const note = encodeURIComponent(
    `GODSC Application Fee - ${applicantName || "Applicant"}`
  );
  return `https://cash.app/$godscinc/${APPLICATION_FEE}?note=${note}`;
};

const PROSPECTIVE_LONG_FIELDS = [
  { key: "honors_awards", label: "Please list all High School honors, awards, and scholarships" },
  { key: "future_plans", label: "Briefly describe your plans for the future" },
  { key: "about_yourself", label: "Briefly describe yourself" },
  { key: "howard_experience", label: "Tell us what you would like out of your Howard experience / education" },
  { key: "proposed_event", label: "Outline one event you would run under GODSC" },
  { key: "tangible_skills", label: "What tangible skills can you provide to The Gentlemen of Drew Social Club Inc.?" },
  { key: "why_godsc", label: "Why do you want to be a member of GODSC?" },
];

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    classification: "",
    major: "",
    dorm: "",
    message: "",
    interest_type: APPLICATIONS_OPEN ? "prospective" : "contact",
    honors_awards: "",
    future_plans: "",
    about_yourself: "",
    howard_experience: "",
    proposed_event: "",
    tangible_skills: "",
    why_godsc: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // null | {name, interest_type}

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/interest", form);
      setSubmitted({ id: data.id, name: form.name, interest_type: form.interest_type });
      toast.success("Submission received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(null);
    setForm({
      name: "",
      email: "",
      phone: "",
      classification: "",
      major: "",
      dorm: "",
      message: "",
      interest_type: "prospective",
    });
  };

  return (
    <div>
      <PageHero
        overline="Interest & Contact"
        title="Begin the Conversation."
        subtitle="Prospective members, alumni, and partners &mdash; we read every submission."
      />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-8">
            <div>
              <div className="overline text-[#C9A227] mb-3">Address</div>
              <div className="flex items-start gap-3 text-[#24324F]">
                <MapPin size={18} className="mt-1 text-[#0A1F44]" />
                <div>
                  Drew Hall, Howard University
                  <br />
                  511 Gresham Pl NW
                  <br />
                  Washington, D.C. 20059
                </div>
              </div>
            </div>
            <div>
              <div className="overline text-[#C9A227] mb-3">Email</div>
              <a
                href="mailto:Godscinc1982@gmail.com"
                className="flex items-center gap-3 text-[#0A1F44] hover:text-[#C9A227]"
                data-testid="contact-email-link"
              >
                <Mail size={18} /> Godscinc1982@gmail.com
              </a>
            </div>
            <div>
              <div className="overline text-[#C9A227] mb-3">Social</div>
              <a
                href="https://instagram.com/godscinc"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-[#0A1F44] hover:text-[#C9A227]"
                data-testid="contact-ig"
              >
                <Instagram size={18} /> @godscinc
              </a>
            </div>
            <div className="bg-[#0A1F44] text-white p-8">
              <div className="overline text-[#D4AF37] mb-3">A Note to Prospectives</div>
              {APPLICATIONS_OPEN ? (
                <p className="text-sm leading-relaxed text-[#D8D3C4]">
                  A non-refundable <strong className="text-white">${APPLICATION_FEE} application fee</strong>{" "}
                  is required after submitting your interest form. The next step
                  will be presented to you immediately upon submission.
                </p>
              ) : (
                <p className="text-sm leading-relaxed text-[#D8D3C4]">
                  Applications are <strong className="text-white">currently closed</strong>.
                  Interest for the next line will open at the start of the
                  academic year &mdash; follow <strong className="text-white">@godscinc</strong>{" "}
                  for announcements.
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            {submitted ? (
              <SubmittedView submitted={submitted} onReset={resetForm} />
            ) : (
              <form
                onSubmit={submit}
                className="bg-white border border-[#D8D3C4] p-8 md:p-10"
                data-testid="interest-form"
              >
                <div className="flex items-center gap-3 mb-8 flex-wrap">
                  <label className="overline text-[#5C5C5C]">Type of inquiry:</label>
                  <div className="flex gap-2">
                    {["prospective", "contact"].map((t) => {
                      const isLocked = !APPLICATIONS_OPEN && t === "prospective";
                      return (
                        <button
                          key={t}
                          type="button"
                          disabled={isLocked}
                          onClick={() => !isLocked && setForm((f) => ({ ...f, interest_type: t }))}
                          className={`px-4 py-2 text-xs tracking-[0.22em] uppercase border transition-colors ${
                            isLocked
                              ? "bg-[#F5F1E6] text-[#9A9A9A] border-[#D8D3C4] cursor-not-allowed"
                              : form.interest_type === t
                              ? "bg-[#0A1F44] text-white border-[#0A1F44]"
                              : "bg-white text-[#0A1F44] border-[#D8D3C4] hover:border-[#0A1F44]"
                          }`}
                          data-testid={`interest-type-${t}`}
                        >
                          {t === "prospective"
                            ? (isLocked ? "Prospective (Closed)" : "Prospective")
                            : "General"}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {!APPLICATIONS_OPEN && (
                  <div className="mb-6 border border-[#D4AF37] bg-[#FBF6E4] px-4 py-3 text-sm text-[#0A1F44]" data-testid="applications-closed-notice">
                    <strong>Applications are currently closed.</strong> You can still send a general inquiry using the form below. Follow <a href="https://instagram.com/godscinc" target="_blank" rel="noreferrer" className="underline hover:text-[#C9A227]">@godscinc</a> for updates on when applications reopen.
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name *" testid="field-name">
                    <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="input-name" />
                  </Field>
                  <Field label="Email *" testid="field-email">
                    <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="input-email" />
                  </Field>
                  <Field label="Phone" testid="field-phone">
                    <input value={form.phone} onChange={update("phone")} className={inputCls} data-testid="input-phone" />
                  </Field>
                  <Field label="Classification" testid="field-classification">
                    <select value={form.classification} onChange={update("classification")} className={inputCls} data-testid="input-classification">
                      <option value="">Select...</option>
                      <option>Freshman</option>
                      <option>Sophomore</option>
                      <option>Junior</option>
                      <option>Senior</option>
                      <option>Alumnus</option>
                      <option>Other</option>
                    </select>
                  </Field>
                  <Field label="Major" testid="field-major">
                    <input value={form.major} onChange={update("major")} className={inputCls} data-testid="input-major" />
                  </Field>
                  <Field label="Dorm / Residence" testid="field-dorm">
                    <input value={form.dorm} onChange={update("dorm")} className={inputCls} data-testid="input-dorm" />
                  </Field>
                </div>
                <Field label="Message" testid="field-message" className="mt-5">
                  <textarea rows={5} value={form.message} onChange={update("message")} className={inputCls} data-testid="input-message" />
                </Field>

                {form.interest_type === "prospective" && (
                  <div className="mt-10" data-testid="prospective-essays">
                    <div className="border-t border-[#D8D3C4] pt-8 mb-6">
                      <div className="overline text-[#C9A227] mb-2">Application Essays</div>
                      <h3 className="font-serif-display text-2xl text-[#0A1F44]">
                        Tell us about yourself.
                      </h3>
                      <p className="text-sm text-[#5C5C5C] mt-2">
                        These responses help the Executive Board get to know you.
                        Please be thoughtful and complete.
                      </p>
                    </div>
                    <div className="space-y-5">
                      {PROSPECTIVE_LONG_FIELDS.map((f) => (
                        <Field
                          key={f.key}
                          label={f.label}
                          testid={`field-${f.key}`}
                        >
                          <textarea
                            rows={4}
                            value={form[f.key]}
                            onChange={update(f.key)}
                            className={inputCls}
                            data-testid={`input-${f.key}`}
                            placeholder="Your response..."
                          />
                        </Field>
                      ))}
                    </div>
                  </div>
                )}

                {form.interest_type === "prospective" && (
                  <div className="mt-6 border border-[#D4AF37] bg-[#FBF6E4] px-4 py-3 text-sm text-[#0A1F44]" data-testid="fee-notice">
                    A <strong>${APPLICATION_FEE} application fee</strong> is required after submission.
                    You&rsquo;ll be directed to Cash App on the next screen.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary-navy mt-8 disabled:opacity-60"
                  data-testid="submit-interest"
                >
                  {submitting
                    ? "Submitting..."
                    : form.interest_type === "prospective"
                    ? "Submit Application"
                    : "Submit Inquiry"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const SubmittedView = ({ submitted, onReset }) => {
  const isProspective = submitted.interest_type === "prospective";
  const cashAppUrl = buildCashAppUrl(submitted.name);

  const openCashApp = () => {
    // Fire-and-forget mark on backend so admin can see "Clicked Pay" status
    api.post(`/interest/${submitted.id}/payment-clicked`).catch(() => {});
    window.open(cashAppUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white border border-[#D8D3C4] p-8 md:p-12" data-testid="submission-success">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[#0A1F44] text-[#D4AF37] flex items-center justify-center shrink-0">
          <CheckCircle2 size={22} />
        </div>
        <div>
          <div className="overline text-[#C9A227]">Submission Received</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#0A1F44] mt-2 leading-tight">
            Thank you, {submitted.name.split(" ")[0]}.
          </h2>
        </div>
      </div>

      <div className="mt-6 h-[2px] w-16 bg-[#D4AF37]" />

      {isProspective ? (
        <>
          <p className="mt-6 text-[#24324F] leading-[1.85]">
            Your interest form has been received by the Executive Board. To
            complete your application, please submit the{" "}
            <strong>${APPLICATION_FEE} non-refundable application fee</strong>{" "}
            via Cash App. Your application is not considered complete until the
            fee is paid.
          </p>

          <div className="mt-8 bg-[#0A1F44] text-white p-6 md:p-8 border border-[#D4AF37]/40">
            <div className="overline text-[#D4AF37] mb-2">Step 2 of 2</div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <div className="font-serif-display text-3xl">
                  Pay ${APPLICATION_FEE} Application Fee
                </div>
                <div className="text-sm text-[#D8D3C4] mt-2">
                  Cash App will open in a new tab. Send to{" "}
                  <span className="text-[#D4AF37] font-medium">$godscinc</span>{" "}
                  &mdash; your name is pre-filled in the note.
                </div>
              </div>
              <button
                type="button"
                onClick={openCashApp}
                className="btn-gold inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="cashapp-pay-btn"
              >
                Pay via Cash App <ExternalLink size={14} />
              </button>
            </div>
          </div>

          <div className="mt-6 text-xs text-[#5C5C5C] leading-relaxed">
            Trouble opening Cash App? You can also pay directly at{" "}
            <a
              href={cashAppUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[#0A1F44] underline hover:text-[#C9A227]"
              data-testid="cashapp-fallback-link"
              onClick={() => {
                api.post(`/interest/${submitted.id}/payment-clicked`).catch(() => {});
              }}
            >
              cash.app/$godscinc
            </a>
            . When paying, please keep <strong>&ldquo;{submitted.name}&rdquo;</strong>{" "}
            in the note so the E-Board can match your payment to your application.
          </div>
        </>
      ) : (
        <p className="mt-6 text-[#24324F] leading-[1.85]">
          Your message has been received. The Executive Board reads every
          submission and will be in touch as soon as possible.
        </p>
      )}

      <button
        type="button"
        onClick={onReset}
        className="btn-outline-navy mt-8"
        data-testid="submit-another"
      >
        Submit Another
      </button>
    </div>
  );
};

const inputCls =
  "w-full border border-[#D8D3C4] bg-white px-3 py-3 text-[#0A1F44] focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 rounded-none font-sans-body text-sm";

const Field = ({ label, children, testid, className = "" }) => (
  <label className={`block ${className}`} data-testid={testid}>
    <span className="overline text-[#5C5C5C] mb-2 block">{label}</span>
    {children}
  </label>
);

export default ContactPage;
